'use client';

import { defineMessages, useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { ProductListSeoFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListSeoFragmentFragmentDoc';
import type { SearchResultPageDefaultContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchResultPageDefaultContentFragmentFragmentDoc';
import type { CmsDataJsonApi } from '@packages/cms-components/interfaces';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import type { Device } from '@packages/shared/src/utils/types';

import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';
import { ProductListPageLayout } from '../ProductListPageLayout/ProductListPageLayout.client';
import { SearchResultHeader } from '../SearchResultHeader/SearchResultHeader';
import { SearchResultCategoryFilter } from '../SearchResultCategoryFilter/SearchResultCategoryFilter';
import { CmsTopBanner } from '../CmsTopBanner/CmsTopBanner';
import { ProductListSeo } from '../ProductListSeo/ProductListSeo';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { bypassProductListPageLayoutMessages } from '../../bypassBlocks.messages';
import { SearchResultPageRecommendationsDynamic } from '../SearchResultPageRecommendations/SearchResultPageRecommendations.dynamic';
import { SearchFeedbackDynamic } from '../SearchFeedback/SearchFeedback.dynamic';
import { SearchKimRatingDynamic } from '../SearchKimRating/SearchKimRating.dynamic';
import { useFeatureFlag } from '../../utils/featureFlags/useFeatureFlag';
import { filtersV2 } from '../../activeFeatureFlags';

/* GraphQL */ `
  fragment SearchResultPageDefaultContentFragment on SuccessfulSearchResult {
    ...SearchResultHeaderFragment
    ...ProductListPageLayoutFragment
    ...SearchResultCategoryFilterFragment
    ...SearchKimRatingFragment
    ...UseDisplayModeFragment
  }
`;
const messages = defineMessages({
  categoryFiltersLabel: {
    id: 'serp.categories.filters.label',
    defaultMessage: 'Kategorienfilter',
  },
});

export type SearchResultPageDefaultContentProps = {
  maskedData: FragmentType<typeof SearchResultPageDefaultContentFragmentFragmentDoc>;
  topLevelCategories?: string[];
  maskedSeoResponse?: FragmentType<typeof ProductListSeoFragmentFragmentDoc>;
  bannerData?: CmsDataJsonApi;
  device: Device;
  referrer?: string;
};

/**
 * SearchResultPageDefaultContent component description displayed in storybook
 * */
export const SearchResultPageDefaultContent = ({
  maskedData,
  topLevelCategories,
  maskedSeoResponse,
  bannerData,
  device,
  referrer,
}: SearchResultPageDefaultContentProps) => {
  const { searchResultUserFeedback } = useConfig();
  const originalQuery = useOriginalSearchQuery();
  const { formatMessage } = useIntl();
  const isFiltersV2Enabled = useFeatureFlag(filtersV2);

  const data = unmask<typeof SearchResultPageDefaultContentFragmentFragmentDoc>(maskedData);

  const { displayMode } = useDisplayMode(data);

  return (
    <ProductListPageLayout
      leftSidebarAriaLabel={formatMessage(messages.categoryFiltersLabel)}
      maskedData={data}
      topLevelCategories={topLevelCategories}
      device={device}
      referrer={referrer}
      enablePRankTracking={false}
      aboveMainContentComponent={
        <>
          {bannerData && <CmsTopBanner bannerData={bannerData} />}
          <SearchResultHeader maskedData={data} originalQuery={originalQuery} />
        </>
      }
      belowProductsComponent={
        <>
          <ErrorBoundary>
            <SearchResultPageRecommendationsDynamic />
          </ErrorBoundary>
          {searchResultUserFeedback.enabled && <SearchFeedbackDynamic />}
          <ErrorBoundary>
            <SearchKimRatingDynamic
              maskedData={data}
              searchPageType="SERP"
              displayMode={displayMode}
              templateParams={{ x17: referrer ?? 'x' }}
            />
          </ErrorBoundary>
          {maskedSeoResponse && <ProductListSeo maskedData={maskedSeoResponse} />}
        </>
      }
      leftSidebarDesktopComponent={
        <BypassBlock
          buttonText={formatMessage(bypassProductListPageLayoutMessages.categoryNavigation)}
          skipScrollIntoView
        >
          <SearchResultCategoryFilter maskedData={data} />
        </BypassBlock>
      }
      renderCategoryDialogContent={
        isFiltersV2Enabled
          ? undefined
          : (closeDialog) => (
              <SearchResultCategoryFilter maskedData={data} onNavigate={closeDialog} />
            )
      }
    />
  );
};
